import { useEffect, useMemo, useState } from "react";
import { Toaster } from "sonner";

import useTasks from "../../Sevices/useTasks";
import useDriver from "../../Sevices/Driver Api/useDriver";
import useDriverTeams from "../../Sevices/Driver Api/useDriverTeams";

import AssignModal from "../MainDashboard/AssignModal";
import TaskDetailsModal from "../MainDashboard/MapDashBoard/TaskDetailsModal";

import TaskTab from "./TaskTab";
import TaskSearchTab from "./TaskSearchTab";

export default function TasksPage() {
  const {
    fetchTasks,
    changeTaskStatus,
    fetchTaskLogs,
    taskGlobalSearch,
    fetchTaskDetails,
    loading,
    globalSearchLoading,
  } = useTasks();

  const { fetchDrivers, AssignTaskToDriver } = useDriver();

  const { teams, fetchDriverTeams } = useDriverTeams();

  // =====================================================
  // MAIN TAB
  // =====================================================

  const [activeTab, setActiveTab] = useState("tasks");

  // =====================================================
  // TASK TAB STATES
  // =====================================================

  const [tasks, setTasks] = useState([]);

  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedTask, setSelectedTask] = useState(null);

  const [assignmentLogs, setAssignmentLogs] = useState([]);

  const [statusChangeLogs, setStatusChangeLogs] = useState([]);

  const [showLogs, setShowLogs] = useState(false);

  const [pageInfo, setPageInfo] = useState({
    nextToken: "",
    previousToken: "",
    pageSize: 50,
    totalCount: 0,
    pageNumber: 1,
  });

  // =====================================================
  // SEARCH TAB STATES
  // =====================================================

  const [taskSearchWord, setTaskSearchWord] = useState("");

  const [taskSearchResults, setTaskSearchResults] = useState([]);

  const [hasTaskSearched, setHasTaskSearched] = useState(false);

  const [taskDetailsLoading, setTaskDetailsLoading] = useState(false);

  const [searchAssignmentLogs, setSearchAssignmentLogs] = useState([]);

  const [searchStatusChangeLogs, setSearchStatusChangeLogs] = useState([]);

  const [searchLogsLoading, setSearchLogsLoading] = useState(false);

  const [selectedSearchStatus, setSelectedSearchStatus] = useState("");

  const [searchStatusUpdating, setSearchStatusUpdating] = useState(false);

  // =====================================================
  // ASSIGN DRIVER STATES
  // =====================================================

  const [agents, setAgents] = useState([]);

  const [showAssignModal, setShowAssignModal] = useState(false);

  const [taskToAssign, setTaskToAssign] = useState(null);

  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [assignTab, setAssignTab] = useState("team");

  const [selectedAssignTeam, setSelectedAssignTeam] = useState("");

  // =====================================================
  // DATE FILTER
  // =====================================================

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const today = new Date();

  const [fromDate, setFromDate] = useState(formatDate(today));

  const [toDate, setToDate] = useState(formatDate(today));

  const [statusFilter, setStatusFilter] = useState("");

  const perPage = 50;

  // =====================================================
  // ASSIGN CONTEXT
  // =====================================================

  const handleAssignContext = (task) => {
    setTaskToAssign(task);

    setSelectedAgentId("");

    const taskShopCode =
      task?.shopcode || task?.shop_code || task?.restaurant_code;

    const matchedTeam = teams?.find((team) =>
      team.shopList?.some(
        (shop) => shop.shop_code?.toString() === taskShopCode?.toString(),
      ),
    );

    if (matchedTeam) {
      setSelectedAssignTeam(matchedTeam.team_name);

      setAssignTab("team");
    } else {
      setAssignTab("all");
    }

    setShowAssignModal(true);
  };

  // =====================================================
  // TEAM DRIVERS
  // =====================================================

  const teamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find(
      (team) => team.team_name === selectedAssignTeam,
    );

    const ids =
      teamData?.driverlist?.map((driver) => driver.id?.toString()) || [];

    return (agents || []).filter(
      (agent) =>
        ids.includes(agent?.id?.toString()) ||
        ids.includes(agent?.driverId?.toString()),
    );
  }, [selectedAssignTeam, teams, agents]);

  const allTeamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find(
      (team) => team.team_name === selectedAssignTeam,
    );

    const ids =
      teamData?.driverlist?.map((driver) => driver.id?.toString()) || [];

    return (agents || []).filter(
      (agent) =>
        ids.includes(agent?.id?.toString()) ||
        ids.includes(agent?.driverId?.toString()),
    );
  }, [selectedAssignTeam, teams, agents]);

  // =====================================================
  // TASK SEARCH BY ID
  // =====================================================

const handleTaskDetailsSearch = async ({
  word = "",
  fromDate = "",
  toDate = "",
} = {}) => {
  try {
    setHasTaskSearched(true);
    setTaskSearchResults([]);

    // =====================================================
    // 1. NEW GLOBAL SEARCH API
    // =====================================================

    const globalResults = await taskGlobalSearch({
      word,
      fromDate,
      toDate,
    });

    const results = Array.isArray(globalResults) ? globalResults : [];

    if (results.length === 0) {
      setTaskSearchResults([]);
      setSearchAssignmentLogs([]);
      setSearchStatusChangeLogs([]);
      return;
    }

    // =====================================================
    // 2. GET TASK ID
    // =====================================================

    const firstTask = results[0];

    const taskId =
      firstTask?.pk_task_id ||
      firstTask?.taskId ||
      firstTask?.task_id ||
      firstTask?.id ||
      word;

    if (!taskId) {
      setTaskSearchResults(results);
      return;
    }

    // =====================================================
    // 3. OLD APIs MUST STILL RUN
    // =====================================================

    const [taskDetails, logs] = await Promise.all([
      // API 1 -> getTaskDetails
      fetchTaskDetails(taskId),

      // API 2 -> getTaskAssignmentLogs
      // API 3 -> getTaskStatusChangeLogs
      fetchTaskLogs(taskId, fromDate, toDate),
    ]);

    // =====================================================
    // RESULT
    // =====================================================

    if (taskDetails) {
      const detailsArray = Array.isArray(taskDetails)
        ? taskDetails
        : [taskDetails];

      setTaskSearchResults(detailsArray);
    } else {
      setTaskSearchResults(results);
    }

    setSearchAssignmentLogs(logs?.assignmentLogs || []);

    setSearchStatusChangeLogs(logs?.statusChangeLogs || []);
  } catch (error) {
    console.error("TASK SEARCH ERROR:", error);

    setTaskSearchResults([]);
    setSearchAssignmentLogs([]);
    setSearchStatusChangeLogs([]);
  }
};
  // =====================================================
  // SEARCH STATUS UPDATE
  // =====================================================

  const handleSearchStatusChange = async (taskItem) => {
    const taskId =
      taskItem?.pk_task_id ||
      taskItem?.taskId ||
      taskItem?.task_id ||
      taskItem?.id;

    if (!taskId || !selectedSearchStatus) {
      return;
    }

    const currentStatus = taskItem?.status?.toLowerCase();

    if (
      selectedSearchStatus === "unassigned" &&
      currentStatus !== "unassigned"
    ) {
      setTaskToAssign(taskItem);

      setSelectedAgentId("");

      setAssignTab("all");

      handleAssignContext(taskItem);

      return;
    }

    setSearchStatusUpdating(true);

    try {
      await changeTaskStatus(taskItem, selectedSearchStatus, async () => {
        const updatedTask = await fetchTaskDetails(taskId);

        const safeData = Array.isArray(updatedTask)
          ? updatedTask
          : updatedTask
            ? [updatedTask]
            : [];

        setTaskSearchResults(safeData);

        if (safeData?.[0]?.status) {
          setSelectedSearchStatus(safeData[0].status.toLowerCase());
        } else {
          setSelectedSearchStatus("");
        }

        if (safeData?.[0]) {
          await fetchSearchLogsDirect(safeData[0]);
        } else {
          setSearchAssignmentLogs([]);

          setSearchStatusChangeLogs([]);
        }
      });
    } catch (error) {
      console.error("Search status update error:", error);
    } finally {
      setSearchStatusUpdating(false);
    }
  };

  // =====================================================
  // TASK TAB GLOBAL SEARCH
  // =====================================================

  const handleTaskTabGlobalSearch = async () => {
    const keyword = search.trim();

    if (!keyword) {
      fetchTasks(fromDate, toDate, setTasks, {
        pageSize: pageInfo.pageSize,
        nextToken: "",
        previousToken: "",
        setPageInfo,
      });

      return;
    }

    const data = await taskGlobalSearch({
      word: keyword,
      fromDate,
      toDate,
    });

    const safeData = Array.isArray(data) ? data : data ? [data] : [];

    setTasks(safeData);

    setCurrentPage(1);
  };

  // =====================================================
  // FILTER TASKS
  // =====================================================

  const filtered = tasks.filter(
    (task) => !statusFilter || task.status === statusFilter,
  );

  const current = filtered;

  // =====================================================
  // GET ONLY DATE
  // =====================================================

  const getOnlyDate = (value) => {
    if (!value) {
      return formatDate(new Date());
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return value?.toString()?.split("T")?.[0] || formatDate(new Date());
    }

    return date.toISOString().split("T")[0];
  };

  // =====================================================
  // FETCH SEARCH LOGS
  // =====================================================

  const fetchSearchLogsDirect = async (taskItem) => {
    const id =
      taskItem?.pk_task_id ||
      taskItem?.taskId ||
      taskItem?.task_id ||
      taskItem?.id;

    if (!id) return;

    setSearchLogsLoading(true);

    try {
      const logDate = getOnlyDate(
        taskItem?.date_created ||
          taskItem?.delivery_date ||
          taskItem?.date_modified,
      );

      const data = await fetchTaskLogs(id, logDate, logDate);

      console.log("Search Logs Response:", data);

      setSearchAssignmentLogs(data?.assignmentLogs || []);

      setSearchStatusChangeLogs(data?.statusChangeLogs || []);
    } catch (error) {
      console.error("Search task logs error:", error);

      setSearchAssignmentLogs([]);

      setSearchStatusChangeLogs([]);
    } finally {
      setSearchLogsLoading(false);
    }
  };

  // =====================================================
  // APPLY FILTER
  // =====================================================

  const handleApplyFilters = () => {
    setCurrentPage(1);

    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: "",
      setPageInfo,
    });
  };

  // =====================================================
  // INITIAL TASK FETCH
  // =====================================================

  useEffect(() => {
    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: "",
      setPageInfo,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // PAGE VALIDATION
  // =====================================================

  useEffect(() => {
    const total = Math.max(1, Math.ceil(filtered.length / perPage));

    if (currentPage > total) {
      setCurrentPage(total);
    }
  }, [filtered.length, currentPage]);

  // =====================================================
  // DRIVERS + TEAMS
  // =====================================================

  useEffect(() => {
    fetchDriverTeams();

    fetchDrivers(true).then(setAgents);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // SERVER PAGINATION
  // =====================================================

  const handleNextPage = () => {
    if (!pageInfo.nextToken) return;

    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: pageInfo.nextToken,
      previousToken: "",
      setPageInfo,
    });
  };

  const handlePreviousPage = () => {
    if (!pageInfo.previousToken) return;

    fetchTasks(fromDate, toDate, setTasks, {
      pageSize: pageInfo.pageSize,
      nextToken: "",
      previousToken: pageInfo.previousToken,
      setPageInfo,
    });
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="h-full flex flex-col overflow-hidden p-6">
      <Toaster position="top-right" />

      {/* =================================================
          TABS
      ================================================= */}

      <div className="shrink-0 mb-5 border-b border-gray-200">
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={() => setActiveTab("tasks")}
            className={`relative pb-3 text-sm font-medium transition ${
              activeTab === "tasks"
                ? "text-orange-600"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Tasks
            {activeTab === "tasks" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-orange-500" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("search")}
            className={`relative pb-3 text-sm font-medium transition ${
              activeTab === "search"
                ? "text-orange-600"
                : "text-gray-500 hover:text-orange-600"
            }`}
          >
            Search By Id
            {activeTab === "search" && (
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-orange-500" />
            )}
          </button>
        </div>
      </div>

      {/* =================================================
          TASK TAB
      ================================================= */}

      {activeTab === "tasks" && (
        <TaskTab
          tasks={tasks}
          current={current}
          filtered={filtered}
          loading={loading}
          globalSearchLoading={globalSearchLoading}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          search={search}
          setSearch={setSearch}
          fetchTasks={fetchTasks}
          setTasks={setTasks}
          pageInfo={pageInfo}
          setPageInfo={setPageInfo}
          handleApplyFilters={handleApplyFilters}
          handleTaskTabGlobalSearch={handleTaskTabGlobalSearch}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          setSelectedTask={setSelectedTask}
          setAssignmentLogs={setAssignmentLogs}
          setStatusChangeLogs={setStatusChangeLogs}
          setShowLogs={setShowLogs}
        />
      )}

      {/* =================================================
          SEARCH TAB
      ================================================= */}

      {activeTab === "search" && (
        <TaskSearchTab
          taskSearchWord={taskSearchWord}
          setTaskSearchWord={setTaskSearchWord}
          taskSearchResults={taskSearchResults}
          setTaskSearchResults={setTaskSearchResults}
          hasTaskSearched={hasTaskSearched}
          setHasTaskSearched={setHasTaskSearched}
          taskDetailsLoading={taskDetailsLoading}
          handleTaskDetailsSearch={handleTaskDetailsSearch}
          selectedSearchStatus={selectedSearchStatus}
          setSelectedSearchStatus={setSelectedSearchStatus}
          searchStatusUpdating={searchStatusUpdating}
          handleSearchStatusChange={handleSearchStatusChange}
          searchLogsLoading={searchLogsLoading}
          searchAssignmentLogs={searchAssignmentLogs}
          searchStatusChangeLogs={searchStatusChangeLogs}
        />
      )}

      {/* =================================================
          TASK DETAILS MODAL
      ================================================= */}

      <TaskDetailsModal
        show={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        assignmentLogs={assignmentLogs}
        statusChangeLogs={statusChangeLogs}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        onFetchLogs={async (taskId) => {
          const data = await fetchTaskLogs(taskId, fromDate, toDate);

          setAssignmentLogs(data?.assignmentLogs || []);

          setStatusChangeLogs(data?.statusChangeLogs || []);
        }}
        onStatusChange={async (taskId, newStatus) => {
          await changeTaskStatus(selectedTask, newStatus, () =>
            fetchTasks(fromDate, toDate, setTasks),
          );

          setSelectedTask(null);
        }}
      />

      {/* =================================================
          ASSIGN DRIVER MODAL
      ================================================= */}

      <AssignModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        teams={teams}
        assignTab={assignTab}
        setAssignTab={setAssignTab}
        selectedAssignTeam={selectedAssignTeam}
        setSelectedAssignTeam={setSelectedAssignTeam}
        teamDrivers={teamDrivers}
        allTeamDrivers={allTeamDrivers}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        handleAssignTask={async () => {
          if (!selectedAgentId || !taskToAssign) {
            return;
          }

          try {
            await changeTaskStatus(taskToAssign, "unassigned");

            const success = await AssignTaskToDriver(
              taskToAssign.pk_task_id,
              selectedAgentId,
            );

            if (success) {
              fetchTasks(fromDate, toDate, setTasks, {
                pageSize: pageInfo.pageSize,
                nextToken: "",
                previousToken: "",
                setPageInfo,
              });

              // Refresh Search By Id
              const taskId =
                taskToAssign?.pk_task_id ||
                taskToAssign?.taskId ||
                taskToAssign?.task_id ||
                taskToAssign?.id;

              if (activeTab === "search" && taskId) {
                const updatedTask = await fetchTaskDetails(taskId);

                const safeData = Array.isArray(updatedTask)
                  ? updatedTask
                  : updatedTask
                    ? [updatedTask]
                    : [];

                setTaskSearchResults(safeData);

                if (safeData?.[0]?.status) {
                  setSelectedSearchStatus(safeData[0].status.toLowerCase());
                }

                setSearchAssignmentLogs([]);

                setSearchStatusChangeLogs([]);

                if (safeData?.[0]) {
                  await fetchSearchLogsDirect(safeData[0]);
                }
              }

              setShowAssignModal(false);

              setSelectedAgentId("");

              setTaskToAssign(null);
            }
          } catch (error) {
            console.error("Assign Flow Error:", error);
          }
        }}
      />
    </div>
  );
}
